import { useToast } from 'contexts/ToastContext';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Mail,
  Pause,
  Play,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import DateRangePicker from '../../components/ui/DateRangePicker';
import { CAMPAIGN_DATE_PRESETS } from '../../constants';
import { CampaignTableSkeleton } from '../../skeletons';
import { AppDispatch } from '../../store';
import {
  clearCampaignState,
  createCampaignRequest,
  duplicateCampaignRequest,
  getCampaignsRequest,
  selectCampaigns,
  selectCampaignsLoading,
  selectCampaignsPagination,
  selectCampaignSuccess,
  selectCreateCampaignLoading,
  deleteCampaignRequest,
  selectDeleteCampaignLoading,
  selectDeleteCampaignSuccess,
  clearDeleteState,
  pauseCampaignRequest,
  publishCampaignRequest,
  selectPauseCampaignSuccess,
  selectPauseCampaignLoading,
  selectPauseCampaignError,
  selectCampaignsError,
  clearPauseCampaignState,
  selectPublishCampaignSuccess,
  clearPublishCampaignState,
  selectPublishCampaignLoading,
} from '../../store/campaigns';
import { CampaignFilters } from '../../types/campaigns/campaigns.types';
import Loader from 'components/ui/Loader';

const toDateOnlyString = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getLast30DaysRange = () => {
  const today = new Date();
  const endDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 29
  );
  return { startDate, endDate };
};

const CampaignsV2: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  const loading = useSelector(selectCampaignsLoading);
  const campaigns = useSelector(selectCampaigns);
  const pagination = useSelector(selectCampaignsPagination);
  const createLoading = useSelector(selectCreateCampaignLoading);
  const campaignPausedSuccess = useSelector(selectPauseCampaignSuccess);
  const campaignPausedLoading = useSelector(selectPauseCampaignLoading);
  const campaignPausedError = useSelector(selectPauseCampaignError);
  const publishCampaignSuccess = useSelector(selectPublishCampaignSuccess);
  const publishCampaignLoading = useSelector(selectPublishCampaignLoading);
  const error = useSelector(selectCampaignsError);
  const success = useSelector(selectCampaignSuccess);
  const { showToast } = useToast();

  // Local state
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [filters, setFilters] = useState<CampaignFilters>(() => {
    const { startDate, endDate } = getLast30DaysRange();
    return {
      page: 0,
      limit: 10,
      search: '',
      status: '',
      startDate: toDateOnlyString(startDate),
      endDate: toDateOnlyString(endDate),
    };
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(() => getLast30DaysRange());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [showPauseResumeModal, setShowPauseResumeModal] = useState(false);
  const [campaignToPauseResume, setCampaignToPauseResume] = useState<{
    id: string;
    action: 'pause' | 'resume';
  } | null>(null);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      if (
        pagination &&
        (pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0)
      ) {
        setFilters((prev) => {
          if (
            prev.page !== pagination.page ||
            prev.limit !== pagination.limit
          ) {
            return {
              ...prev,
              page: pagination.page,
              limit: pagination.limit,
            };
          }
          return prev;
        });
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch campaigns when filters change
  useEffect(() => {
    if (isInitialized) {
      dispatch(
        getCampaignsRequest({
          ...filters,
          search: debouncedSearch || undefined,
        })
      );
    }
  }, [dispatch, filters, debouncedSearch, isInitialized]);

  // Handle successful campaign creation
  useEffect(() => {
    if (success) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Campaign created successfully',
      });
      const latestCampaign = campaigns[campaigns.length - 1];
      if (latestCampaign) {
        navigate(`/campaigns/builder/${latestCampaign._id}`);
        dispatch(clearCampaignState());
      }
    }
  }, [success, campaigns, navigate, dispatch, showToast]);

  useEffect(() => {
    if (campaignPausedError || error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error ?? 'Campaign Pause Error',
      });
      setCampaignToPauseResume(null);
      dispatch(clearPauseCampaignState());
    }
  }, [
    campaignPausedError,
    debouncedSearch,
    dispatch,
    error,
    filters,
    showToast,
  ]);
  useEffect(() => {
    if (campaignPausedSuccess || publishCampaignSuccess) {
      setCampaignToPauseResume(null);
      dispatch(clearPauseCampaignState());
      dispatch(clearPublishCampaignState());
      dispatch(
        getCampaignsRequest({
          ...filters,
          search: debouncedSearch || undefined,
        })
      );
    }
  }, [
    campaignPausedSuccess,
    publishCampaignSuccess,
    debouncedSearch,
    dispatch,
    error,
    filters,
    showToast,
  ]);

  // Filter campaigns based on active tab (client-side filtering for tabs only)
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesTab =
      activeTab === 'active'
        ? campaign.status !== 'archived'
        : campaign.status === 'archived';
    return matchesTab;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      live: {
        color: 'bg-success/10 text-success',
        text: 'Live',
        icon: '●',
      },
      draft: {
        color: 'bg-muted/50 text-foreground',
        text: 'Draft',
        icon: '○',
      },
      paused: {
        color: 'bg-warning/10 text-warning',
        text: 'Paused',
        icon: '●',
      },
      archived: {
        color: 'bg-muted/50 text-foreground',
        text: 'Archived',
        icon: '●',
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-2 py-0.5 ${config.color}`}
      >
        <span>{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return (num ?? 0)?.toString();
  };

  const handleCreateCampaign = () => {
    dispatch(createCampaignRequest({ name: 'Untitled Campaign' }));
  };

  const deleting = useSelector(selectDeleteCampaignLoading);
  const deleteSuccess = useSelector(selectDeleteCampaignSuccess);

  const handleDeleteCampaign = (campaignId: string) => {
    setCampaignToDelete(campaignId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!campaignToDelete) return;
    dispatch(
      deleteCampaignRequest(campaignToDelete, {
        page: filters.page,
        limit: filters.limit,
        search: debouncedSearch || undefined,
        status: filters.status || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      })
    );
    setShowDeleteModal(false);
    setCampaignToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCampaignToDelete(null);
  };

  // Show toast on delete success and clear flag
  useEffect(() => {
    if (deleteSuccess) {
      showToast({
        type: 'success',
        title: 'Deleted',
        message: 'Campaign deleted successfully',
      });
      dispatch(clearDeleteState());
    }
  }, [deleteSuccess, dispatch, showToast]);

  const handleDuplicateCampaign = (campaignId: string) => {
    dispatch(duplicateCampaignRequest(campaignId));
  };

  const handlePauseCampaign = (campaignId: string) => {
    setCampaignToPauseResume({ id: campaignId, action: 'pause' });
    setShowPauseResumeModal(true);
  };

  const handleResumeCampaign = (campaignId: string) => {
    setCampaignToPauseResume({ id: campaignId, action: 'resume' });
    setShowPauseResumeModal(true);
  };

  const confirmPauseResume = () => {
    if (!campaignToPauseResume) return;

    if (campaignToPauseResume.action === 'pause') {
      dispatch(pauseCampaignRequest(campaignToPauseResume.id));
    } else {
      dispatch(publishCampaignRequest(campaignToPauseResume.id));
    }

    setShowPauseResumeModal(false);
  };

  const cancelPauseResume = () => {
    setShowPauseResumeModal(false);
    setCampaignToPauseResume(null);
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit,
      page: 0, // Reset to first page when changing page size
    }));
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleDateRangeChange = (range: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    const resolvedRange =
      range.startDate && range.endDate
        ? { startDate: range.startDate, endDate: range.endDate }
        : getLast30DaysRange();
    setDateRange(resolvedRange);
    setFilters((prev) => ({
      ...prev,
      page: 0,
      startDate: toDateOnlyString(resolvedRange.startDate),
      endDate: toDateOnlyString(resolvedRange.endDate),
    }));
  };

  const activeCampaigns = filteredCampaigns.filter(
    (c) => c.status !== 'archived'
  );
  const archivedCampaigns = filteredCampaigns.filter(
    (c) => c.status === 'archived'
  );

  return (
    <div className="p-6 space-y-4 bg-background">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Campaigns</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your email campaigns and track performance
          </p>
        </div>

        {/* Create Campaign Button */}
        <Button
          variant="primary"
          onClick={handleCreateCampaign}
          disabled={createLoading}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {createLoading ? 'Creating...' : 'Create Campaign'}
        </Button>
      </div>

      {/* Search and Filter Controls */}
      {loading ? (
        <div className="h-12 bg-muted/50 rounded-lg animate-pulse" />
      ) : (!loading && filteredCampaigns.length > 0) ||
        debouncedSearch ||
        dateRange ? (
        <div className="flex items-center justify-between gap-4">
          {/* Search Section */}
          <div className="flex-1 max-w-md">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search campaigns by name or description..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-12 pr-4 py-2.5 border border-border rounded-lg bg-card text-card-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
              {search && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Date Range Filter */}
            <div className="w-[320px]">
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                presets={CAMPAIGN_DATE_PRESETS}
                placeholder="Filter by date"
              />
            </div>
            <div className="relative">
              <select
                value={filters.limit?.toString() || '10'}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="appearance-none bg-card border border-border rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Campaigns Count */}
      {!loading && (filteredCampaigns.length > 0 || debouncedSearch) && (
        <div className="text-sm text-muted-foreground">
          {debouncedSearch ? (
            <>
              {pagination.total}{' '}
              {pagination.total === 1 ? 'campaign' : 'campaigns'} found for "
              {debouncedSearch}"
            </>
          ) : (
            <></>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="w-full">
        <div className="w-1/4 flex items-center gap-1 rounded-lg bg-muted/80 p-2">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex justify-center items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full
                      transition-all duration-200
                      ${
                        activeTab === 'active'
                          ? 'bg-primary/80 text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                      }
            `}
          >
            Active ({activeCampaigns.length})
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`flex justify-center items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full
                      transition-all duration-200
                      ${
                        activeTab === 'archived'
                          ? 'bg-primary/80 text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                      }
            `}
          >
            Archived ({archivedCampaigns.length})
          </button>
        </div>
      </div>

      {/* Campaigns Table */}
      {loading ? (
        <CampaignTableSkeleton rows={5} />
      ) : filteredCampaigns.length === 0 ? (
        <div className="bg-card rounded-xl border border-border">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                No campaigns yet
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md">
                Get started by creating your first campaign. You can create
                engaging email campaigns, set up automation workflows, and track
                performance.
              </p>
              <div className="flex justify-center">
                <Button
                  variant="primary"
                  onClick={handleCreateCampaign}
                  disabled={createLoading}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {createLoading ? 'Creating...' : 'Create First Campaign'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[200px]">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[100px]">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Sent
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Failed
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Opened
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Clicked
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                  Converted
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map((campaign) => (
                <tr
                  key={campaign._id}
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap min-w-[200px]">
                    <div className="space-y-1">
                      <Link
                        to={`/campaigns/builder/${campaign._id}`}
                        className="text-sm text-primary hover:text-primary-hover cursor-pointer hover:underline font-medium block"
                      >
                        {campaign.name}
                      </Link>
                      {campaign.description && (
                        <div className="text-sm text-muted-foreground max-w-xs truncate">
                          {campaign.description}
                        </div>
                      )}
                      {campaign.status === 'draft' && (
                        <Link
                          to={`/campaigns/builder/${campaign._id}`}
                          className="text-sm text-primary hover:text-primary-hover cursor-pointer"
                        >
                          Campaign not started. Continue editing →
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap min-w-[100px]">
                    {getStatusBadge(campaign.status)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap min-w-[120px]">
                    <div className="text-sm text-card-foreground">
                      {formatNumber(campaign.sent ?? 0)}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap min-w-[120px]">
                    <div className="text-sm text-card-foreground">
                      {formatNumber(campaign.failed ?? 0)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                    <div className="text-sm text-card-foreground">
                      {(campaign.openRate || 0)?.toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                    <div className="text-sm text-card-foreground">
                      {(campaign.clickRate || 0)?.toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                    <div className="text-sm text-card-foreground">
                      {campaign.converted ?? 0}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap w-20">
                    <div className="flex items-center gap-2">
                      {campaign.status === 'live' && (
                        <button
                          onClick={() => handlePauseCampaign(campaign._id)}
                          className="p-1.5 text-muted-foreground hover:text-warning transition-colors rounded-md hover:bg-warning/10"
                          title="Pause campaign"
                        >
                          {campaignPausedLoading &&
                          campaignToPauseResume?.id === campaign._id ? (
                            <Loader />
                          ) : (
                            <Pause className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {campaign.status === 'paused' && (
                        <button
                          onClick={() => handleResumeCampaign(campaign._id)}
                          className="p-1.5 text-muted-foreground hover:text-success transition-colors rounded-md hover:bg-success/10"
                          title="Resume campaign"
                        >
                          {publishCampaignLoading &&
                          campaignToPauseResume?.id === campaign._id ? (
                            <Loader />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleDuplicateCampaign(campaign._id)}
                        className="p-1.5 text-muted-foreground hover:text-primary transition-colors rounded-md hover:bg-primary/10"
                        title="Duplicate campaign"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign._id)}
                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors rounded-md hover:bg-destructive-background"
                        title="Delete campaign"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredCampaigns.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {pagination.page * pagination.limit + 1}-
            {Math.min(
              (pagination.page + 1) * pagination.limit,
              pagination.total
            )}{' '}
            of {pagination.total} campaigns
          </div>

          <div className="flex items-center space-x-2">
            <button
              className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-sm text-muted-foreground px-3 py-1">
              Page {pagination.page + 1} of {pagination.totalPages}
            </span>

            <button
              className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${pagination.page >= pagination.totalPages - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={pagination.page >= pagination.totalPages - 1}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-[9999]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border">
            <h3 className="text-lg font-medium text-card-foreground mb-4">
              Delete Campaign
            </h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this campaign? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={cancelDelete}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-destructive hover:bg-destructive/90"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPauseResumeModal && campaignToPauseResume && (
        <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-[9999]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border">
            <h3 className="text-lg font-medium text-card-foreground mb-4">
              {campaignToPauseResume.action === 'pause'
                ? 'Pause Campaign'
                : 'Resume Campaign'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {campaignToPauseResume.action === 'pause'
                ? 'Are you sure you want to pause this campaign? It will stop sending emails to recipients.'
                : 'Are you sure you want to resume this campaign? It will start sending emails to recipients again.'}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={cancelPauseResume}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmPauseResume}
                className={
                  campaignToPauseResume.action === 'pause'
                    ? 'bg-warning hover:bg-warning/90'
                    : 'bg-success hover:bg-success/90'
                }
              >
                {campaignToPauseResume.action === 'pause' ? 'Pause' : 'Resume'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignsV2;
